import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { sumBy } from 'es-toolkit/math';
import { useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/utils';
import { getAdminDappPath } from '../../../utils/dappRoutes';
import { formatNullableDate } from '../reportingUtils';
import type {
  ReportingOnboardingKybMetrics,
  ReportingOnboardingWorklistBucket,
  ReportingOnboardingWorklistItem,
} from '../types';
import {
  RatioBar,
  ReportingInset,
  ReportingPanel,
  ReportingSectionHeader,
  ReportingStatusPill,
  Sparkbar,
} from './ReportingChrome';
import { ReportingMetricCard } from './ReportingMetricCard';

interface OnboardingKybSectionProps {
  metrics: ReportingOnboardingKybMetrics;
}

const CALLOUT_TONES: Record<
  ReportingOnboardingKybMetrics['callouts'][number]['tone'],
  {
    container: string;
    title: string;
    pill: 'teal' | 'amber' | 'rose' | 'neutral';
  }
> = {
  teal: {
    container: 'border-teal-500/20 bg-teal-500/[0.06]',
    title: 'text-teal-200',
    pill: 'teal',
  },
  amber: {
    container: 'border-amber-500/20 bg-amber-500/[0.06]',
    title: 'text-amber-200',
    pill: 'amber',
  },
  rose: {
    container: 'border-rose-500/20 bg-rose-500/[0.06]',
    title: 'text-rose-200',
    pill: 'rose',
  },
  neutral: {
    container: 'border-neutral-800/50 bg-neutral-950/30',
    title: 'text-neutral-200',
    pill: 'neutral',
  },
};

const WORKLIST_TONES: Record<ReportingOnboardingWorklistBucket['key'], string> =
  {
    requiresAdditionalInfo: 'text-amber-300',
    staleInProgress: 'text-amber-300',
    submittedButNotAdvanced: 'text-rose-300',
    approvedNoDapp: 'text-neutral-200',
    dappNoPublish: 'text-sky-300',
  };

type SuccessMetric = ReportingOnboardingKybMetrics['successMetrics'][number];

const SUCCESS_RATIO_LABELS: Record<
  SuccessMetric['key'],
  {
    numerator: string;
    denominator: string;
    denominatorText: string;
  }
> = {
  kybApproved: {
    numerator: 'Approved',
    denominator: 'Submitted',
    denominatorText: 'submitted',
  },
  firstDappCreated: {
    numerator: 'Created',
    denominator: 'Approved',
    denominatorText: 'approved',
  },
  firstDappPublished: {
    numerator: 'Published',
    denominator: 'Created',
    denominatorText: 'with a dapp',
  },
  websdkSubmitted: {
    numerator: 'Submitted',
    denominator: 'Ready',
    denominatorText: 'ready sessions',
  },
};

export function OnboardingKybSection({ metrics }: OnboardingKybSectionProps) {
  const defaultBucketKey =
    metrics.worklist.find((bucket) => bucket.count > 0)?.key ??
    metrics.worklist[0]?.key ??
    'requiresAdditionalInfo';
  const [activeBucketKey, setActiveBucketKey] =
    useState<ReportingOnboardingWorklistBucket['key']>(defaultBucketKey);
  const [trackedWorklist, setTrackedWorklist] = useState(metrics.worklist);

  if (metrics.worklist !== trackedWorklist) {
    setTrackedWorklist(metrics.worklist);
    const activeBucketExists = metrics.worklist.some(
      (bucket) => bucket.key === activeBucketKey,
    );
    if (!activeBucketExists) {
      setActiveBucketKey(defaultBucketKey);
    }
  }

  const activeBucket =
    metrics.worklist.find((bucket) => bucket.key === activeBucketKey) ??
    metrics.worklist[0] ??
    null;
  const totalWorklistItems = sumBy(metrics.worklist, (bucket) => bucket.count);
  const cohortSparkValues = metrics.cohortTrend.map(
    (point) => point.publisherJoins,
  );

  return (
    <div className="space-y-6">
      <SuccessMetricCards metrics={metrics} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.9fr)]">
        <ReportingPanel className="overflow-hidden">
          <div className="p-5">
            <ReportingSectionHeader
              title="Publisher onboarding funnel"
              subtitle="Current publisher joins through KYB, first dapp, and first published dapp"
            />
            <FunnelStages metrics={metrics} />
          </div>
        </ReportingPanel>

        <ReportingPanel className="overflow-hidden">
          <div className="p-5">
            <ReportingSectionHeader
              title="Join cohorts"
              subtitle="Publisher profiles created in the selected reporting range"
              trailing={
                cohortSparkValues.length > 0 && (
                  <Sparkbar
                    values={cohortSparkValues}
                    className="w-28"
                    ariaLabel="Publisher join cohorts"
                  />
                )
              }
            />
            <CohortTrend metrics={metrics} />
          </div>
        </ReportingPanel>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <ReportingPanel className="overflow-hidden">
          <div className="p-5">
            <ReportingSectionHeader
              title="Time to stage"
              subtitle="P50 and P95 durations from supported portal timestamps"
            />
            <DurationMetrics metrics={metrics} />
          </div>
        </ReportingPanel>

        <ReportingPanel className="overflow-hidden">
          <div className="p-5">
            <ReportingSectionHeader
              title="Sumsub WebSDK"
              subtitle="Session-level progress and upload errors in the selected range"
              trailing={
                <span className="tabular-nums">
                  {metrics.websdk.summary.sessions.toLocaleString()} sessions
                </span>
              }
            />
            <WebsdkSummary metrics={metrics} />
          </div>
        </ReportingPanel>
      </div>

      <RiskSignals metrics={metrics} totalWorklistItems={totalWorklistItems} />

      <ReportingPanel className="overflow-hidden">
        <div className="p-5">
          <ReportingSectionHeader
            title="Onboarding worklist"
            subtitle="Rows are sorted by age and omit emails, names, phone numbers, and document details"
            trailing={
              <Link
                to="/admin/kyb"
                className="inline-flex items-center gap-1 text-teal-300 hover:text-teal-200"
              >
                <span>Open KYB queue</span>
                <OpenInNewIcon className="size-3.5 fill-current" />
              </Link>
            }
          />
          <Worklist
            buckets={metrics.worklist}
            activeBucket={activeBucket}
            onBucketChange={setActiveBucketKey}
          />
        </div>
      </ReportingPanel>
    </div>
  );
}

function SuccessMetricCards({
  metrics,
}: {
  metrics: ReportingOnboardingKybMetrics;
}) {
  if (metrics.successMetrics.length === 0) {
    return (
      <EmptyState message="No onboarding success metrics are available." />
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {metrics.successMetrics.map((metric) => {
        const ratio = SUCCESS_RATIO_LABELS[metric.key];

        return (
          <ReportingMetricCard
            key={metric.key}
            label={metric.label}
            value={metric.value.toLocaleString()}
            tone={metric.rate === null ? 'amber' : 'teal'}
            emphasis="highlight"
            hint={formatSuccessHint(metric)}
            footer={
              <RatioBar
                numerator={metric.value}
                denominator={metric.denominator ?? 0}
                numeratorLabel={ratio.numerator}
                denominatorLabel={ratio.denominator}
              />
            }
          />
        );
      })}
    </div>
  );
}

function RiskSignals({
  metrics,
  totalWorklistItems,
}: {
  metrics: ReportingOnboardingKybMetrics;
  totalWorklistItems: number;
}) {
  return (
    <ReportingPanel className="overflow-hidden">
      <div className="p-5">
        <ReportingSectionHeader
          title="Risk signals"
          subtitle="Operational queues to check after the success and activation view"
          trailing={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className="tabular-nums text-neutral-500">
                {totalWorklistItems.toLocaleString()} worklist rows
              </span>
              <ReportingStatusPill
                label={formatSyncStatus(metrics.freshness.sumsubLastSyncStatus)}
                tone={
                  metrics.freshness.sumsubSyncHealth === 'healthy'
                    ? 'teal'
                    : metrics.freshness.sumsubSyncHealth === 'error'
                      ? 'rose'
                      : 'amber'
                }
              />
            </div>
          }
        />
        <div className="mt-5 grid gap-2 lg:grid-cols-3">
          {metrics.callouts.map((callout) => {
            const tone = CALLOUT_TONES[callout.tone];

            return (
              <div
                key={`${callout.title}-${callout.detail}`}
                className={cn('rounded-lg border p-3', tone.container)}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className={cn('text-sm font-medium', tone.title)}>
                    {callout.title}
                  </h3>
                  <ReportingStatusPill label={callout.tone} tone={tone.pill} />
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-neutral-400">
                  {callout.detail}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </ReportingPanel>
  );
}

function FunnelStages({ metrics }: { metrics: ReportingOnboardingKybMetrics }) {
  const max = Math.max(...metrics.funnel.map((stage) => stage.count), 1);

  return (
    <div className="mt-5 space-y-3">
      {metrics.funnel.map((stage) => {
        const width = Math.max(3, (stage.count / max) * 100);

        return (
          <div key={stage.key} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-xs">
              <div className="min-w-0">
                <span className="font-medium text-neutral-200">
                  {stage.label}
                </span>
                <span className="ml-2 text-neutral-500">{stage.note}</span>
              </div>
              <div className="shrink-0 text-right tabular-nums text-neutral-300">
                <span className="font-medium text-neutral-200">
                  {stage.count.toLocaleString()}
                </span>
                {stage.previousStageRate !== null && (
                  <span className="ml-2 text-[11px] text-neutral-500">
                    {formatPercent(stage.previousStageRate)}
                  </span>
                )}
              </div>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-neutral-800/60">
              <span
                className="block h-full rounded-full bg-teal-400/70"
                style={{ width: `${width}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CohortTrend({ metrics }: { metrics: ReportingOnboardingKybMetrics }) {
  if (metrics.cohortTrend.length === 0) {
    return (
      <EmptyState message="No publisher profiles were created in this date range." />
    );
  }

  const max = Math.max(
    ...metrics.cohortTrend.flatMap((point) => [
      point.publisherJoins,
      point.kybSubmitted,
      point.kybApproved,
      point.firstDappCreated,
      point.firstDappPublished,
    ]),
    1,
  );

  return (
    <div className="mt-5 space-y-4">
      <div className="grid grid-cols-5 gap-2 text-[11px] text-neutral-500">
        <LegendDot className="bg-neutral-300" label="Joined" />
        <LegendDot className="bg-sky-300" label="Submitted" />
        <LegendDot className="bg-teal-300" label="Approved" />
        <LegendDot className="bg-amber-300" label="Dapp" />
        <LegendDot className="bg-emerald-300" label="Published" />
      </div>
      <div className="space-y-3">
        {metrics.cohortTrend.map((point) => (
          <div
            key={point.week}
            className="grid gap-3 text-xs sm:grid-cols-[90px_minmax(0,1fr)]"
          >
            <div className="pt-1 text-neutral-500">
              {formatNullableDate(point.week, '—')}
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              <CohortBar value={point.publisherJoins} max={max} />
              <CohortBar value={point.kybSubmitted} max={max} tone="sky" />
              <CohortBar value={point.kybApproved} max={max} tone="teal" />
              <CohortBar
                value={point.firstDappCreated}
                max={max}
                tone="amber"
              />
              <CohortBar
                value={point.firstDappPublished}
                max={max}
                tone="emerald"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DurationMetrics({
  metrics,
}: {
  metrics: ReportingOnboardingKybMetrics;
}) {
  return (
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {metrics.durationMetrics.map((metric) => (
        <ReportingInset key={metric.key} className="p-4">
          <div className="text-xs font-medium text-neutral-300">
            {metric.label}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <MetricMini label="P50" value={formatHours(metric.p50Hours)} />
            <MetricMini label="P95" value={formatHours(metric.p95Hours)} />
          </div>
          <div className="mt-3 text-[11px] leading-relaxed text-neutral-500">
            {metric.sampleSize.toLocaleString()} samples. {metric.note}
          </div>
        </ReportingInset>
      ))}
    </div>
  );
}

function WebsdkSummary({
  metrics,
}: {
  metrics: ReportingOnboardingKybMetrics;
}) {
  const summary = metrics.websdk.summary;

  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
      <ReportingInset className="p-4">
        <div className="grid grid-cols-2 gap-3">
          <MetricMini
            label="Loaded"
            value={summary.applicantLoadedSessions.toLocaleString()}
          />
          <MetricMini
            label="Submitted"
            value={summary.applicantSubmittedSessions.toLocaleString()}
          />
          <MetricMini
            label="Errors"
            value={summary.sessionsWithError.toLocaleString()}
          />
          <MetricMini
            label="P95 session"
            value={formatSeconds(summary.p95SessionSeconds)}
          />
        </div>
        <div className="mt-4">
          <RatioBar
            numerator={summary.applicantSubmittedSessions}
            denominator={summary.readySessions}
            numeratorLabel="Submitted"
            denominatorLabel="Ready"
          />
        </div>
      </ReportingInset>

      <div className="space-y-3">
        <StepDropoff steps={metrics.websdk.steps} />
        <WebsdkErrors errors={metrics.websdk.errors} />
      </div>
    </div>
  );
}

function StepDropoff({
  steps,
}: {
  steps: ReportingOnboardingKybMetrics['websdk']['steps'];
}) {
  if (steps.length === 0) {
    return <EmptyState message="No Sumsub step events in this date range." />;
  }

  const max = Math.max(...steps.map((step) => step.initiatedSessions), 1);

  return (
    <ReportingInset className="p-3.5">
      <div className="mb-3 text-[11px] uppercase tracking-wider text-neutral-500">
        Step completion
      </div>
      <div className="space-y-2">
        {steps.map((step) => (
          <div key={step.step} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-300">{step.label}</span>
              <span className="tabular-nums text-neutral-500">
                {formatPercent(step.completionRate)}
              </span>
            </div>
            <div className="relative h-1.5 overflow-hidden rounded-full bg-neutral-800/60">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-sky-400/40"
                style={{
                  width: `${Math.max(3, (step.initiatedSessions / max) * 100)}%`,
                }}
              />
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-teal-400/70"
                style={{
                  width: `${Math.max(0, (step.completedSessions / max) * 100)}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </ReportingInset>
  );
}

function WebsdkErrors({
  errors,
}: {
  errors: ReportingOnboardingKybMetrics['websdk']['errors'];
}) {
  if (errors.length === 0) {
    return <EmptyState message="No WebSDK error events in this date range." />;
  }

  return (
    <ReportingInset className="p-3.5">
      <div className="mb-3 text-[11px] uppercase tracking-wider text-neutral-500">
        Upload errors
      </div>
      <div className="space-y-1.5">
        {errors.map((error) => (
          <div
            key={error.errorCode}
            className="flex items-center justify-between gap-3 text-xs"
          >
            <span className="truncate font-mono text-[11px] text-neutral-300">
              {error.errorCode}
            </span>
            <span className="shrink-0 tabular-nums text-neutral-500">
              {error.events.toLocaleString()} events ·{' '}
              {error.users.toLocaleString()} users
            </span>
          </div>
        ))}
      </div>
    </ReportingInset>
  );
}

function Worklist({
  buckets,
  activeBucket,
  onBucketChange,
}: {
  buckets: ReportingOnboardingWorklistBucket[];
  activeBucket: ReportingOnboardingWorklistBucket | null;
  onBucketChange: (key: ReportingOnboardingWorklistBucket['key']) => void;
}) {
  return (
    <div className="mt-5 space-y-4">
      <div className="grid gap-2 md:grid-cols-5">
        {buckets.map((bucket) => {
          const isActive = activeBucket?.key === bucket.key;
          return (
            <button
              key={bucket.key}
              type="button"
              onClick={() => onBucketChange(bucket.key)}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors',
                isActive
                  ? 'border-teal-500/30 bg-teal-500/[0.06]'
                  : 'border-neutral-800/70 bg-neutral-950/40 hover:border-neutral-700',
              )}
            >
              <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                {bucket.label}
              </div>
              <div
                className={cn(
                  'mt-1.5 text-xl font-semibold tabular-nums',
                  WORKLIST_TONES[bucket.key],
                )}
              >
                {bucket.count.toLocaleString()}
              </div>
              <div className="mt-1 text-[11px] text-neutral-500">
                Oldest {formatDays(bucket.oldestAgeDays)}
              </div>
            </button>
          );
        })}
      </div>

      {activeBucket && <WorklistTable bucket={activeBucket} />}
    </div>
  );
}

function WorklistTable({
  bucket,
}: {
  bucket: ReportingOnboardingWorklistBucket;
}) {
  if (bucket.items.length === 0) {
    return <EmptyState message="No rows are currently in this worklist." />;
  }

  return (
    <ReportingInset className="overflow-hidden">
      <div className="border-b border-neutral-800/70 p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h4 className="text-sm font-medium text-neutral-200">
              {bucket.label}
            </h4>
            <p className="mt-1 text-xs leading-relaxed text-neutral-500">
              {bucket.question} {bucket.note}
            </p>
          </div>
          <div className="text-xs tabular-nums text-neutral-500">
            {bucket.count.toLocaleString()} rows · median age{' '}
            {formatDays(bucket.medianAgeDays)}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-neutral-800/70 text-sm">
          <thead className="bg-neutral-950/40 text-[11px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Publisher</th>
              <th className="px-4 py-2.5 text-left font-medium">KYB</th>
              <th className="px-4 py-2.5 text-left font-medium">Age</th>
              <th className="px-4 py-2.5 text-left font-medium">Activity</th>
              <th className="px-4 py-2.5 text-left font-medium">Dapps</th>
              <th className="px-4 py-2.5 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800/70">
            {bucket.items.map((item) => (
              <WorklistRow
                key={`${bucket.key}-${item.publisherUserJoinId}`}
                item={item}
                actionLabel={bucket.actionLabel}
              />
            ))}
          </tbody>
        </table>
      </div>
    </ReportingInset>
  );
}

function WorklistRow({
  item,
  actionLabel,
}: {
  item: ReportingOnboardingWorklistItem;
  actionLabel: string;
}) {
  const shortJoinId = item.publisherUserJoinId.slice(0, 8);

  return (
    <tr className="text-xs text-neutral-300">
      <td className="px-4 py-3 align-top">
        <div className="font-medium text-neutral-200">{item.publisherType}</div>
        <div className="mt-1 font-mono text-[11px] text-neutral-500">
          {shortJoinId}
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap gap-1.5">
          <ReportingStatusPill
            label={formatStatusLabel(item.kybStatus)}
            tone={statusTone(item.kybStatus)}
          />
          {item.sumsubReviewStatus && (
            <ReportingStatusPill label={item.sumsubReviewStatus} />
          )}
        </div>
        {item.userOnboardingState && (
          <div className="mt-1.5 text-[11px] text-neutral-500">
            User state {item.userOnboardingState}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top tabular-nums text-neutral-400">
        {formatDays(item.ageDays)}
        <div className="mt-1 text-[11px] text-neutral-600">
          Since {formatNullableDate(item.ageStartedAt, 'unknown')}
        </div>
      </td>
      <td className="px-4 py-3 align-top text-neutral-400">
        {formatNullableDate(item.lastActivityAt, 'No activity')}
        {item.submittedAt && (
          <div className="mt-1 text-[11px] text-neutral-600">
            Submitted {formatNullableDate(item.submittedAt)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top tabular-nums text-neutral-400">
        {item.dappCount.toLocaleString()}
        <div className="mt-1 text-[11px] text-neutral-600">
          {item.publishedDappCount.toLocaleString()} published
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex justify-end gap-1.5">
          {item.primaryDappId && (
            <Link
              to={getAdminDappPath({
                id: item.primaryDappId,
                packageName: item.primaryDappPackageName,
              })}
              className="inline-flex items-center gap-1 rounded-md border border-neutral-700/60 px-2 py-1 text-xs text-neutral-300 transition-colors hover:border-neutral-600 hover:text-neutral-100"
            >
              <span>Dapp</span>
              <OpenInNewIcon className="size-3 fill-current" />
            </Link>
          )}
          {item.sumsubApplicantId && (
            <a
              href={`https://cockpit.sumsub.com/checkus/applicant/${item.sumsubApplicantId}/basicInfo?clientId=solana`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-teal-500/25 px-2 py-1 text-xs text-teal-300 transition-colors hover:border-teal-500/40 hover:text-teal-200"
            >
              <span>{actionLabel}</span>
              <OpenInNewIcon className="size-3 fill-current" />
            </a>
          )}
          {!item.sumsubApplicantId && (
            <Link
              to="/admin/kyb"
              className="inline-flex items-center gap-1 rounded-md border border-teal-500/25 px-2 py-1 text-xs text-teal-300 transition-colors hover:border-teal-500/40 hover:text-teal-200"
            >
              <span>{actionLabel}</span>
              <OpenInNewIcon className="size-3 fill-current" />
            </Link>
          )}
        </div>
      </td>
    </tr>
  );
}

function CohortBar({
  value,
  max,
  tone = 'neutral',
}: {
  value: number;
  max: number;
  tone?: 'neutral' | 'sky' | 'teal' | 'amber' | 'emerald';
}) {
  const width = Math.max(value === 0 ? 0 : 8, (value / max) * 100);
  const color = {
    neutral: 'bg-neutral-300',
    sky: 'bg-sky-300',
    teal: 'bg-teal-300',
    amber: 'bg-amber-300',
    emerald: 'bg-emerald-300',
  }[tone];

  return (
    <div className="min-w-0">
      <div className="h-2 overflow-hidden rounded-full bg-neutral-950/70">
        <span
          className={cn('block h-full rounded-full', color)}
          style={{ width: `${width}%` }}
        />
      </div>
      <div className="mt-1 text-right text-[11px] tabular-nums text-neutral-500">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('size-2 rounded-full', className)} />
      {label}
    </span>
  );
}

function MetricMini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium tabular-nums text-neutral-200">
        {value}
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="mt-5 rounded-lg border border-neutral-800/70 bg-neutral-950/40 px-4 py-6 text-center text-sm text-neutral-500">
      {message}
    </div>
  );
}

function formatPercent(value: number | null) {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

function formatSuccessHint(metric: SuccessMetric) {
  if (metric.rate === null || metric.denominator === null) {
    return metric.note;
  }

  const denominatorText = SUCCESS_RATIO_LABELS[metric.key].denominatorText;
  return `${formatPercent(metric.rate)} of ${metric.denominator.toLocaleString()} ${denominatorText}`;
}

function formatSyncStatus(value: string | null) {
  if (!value) {
    return 'Unknown';
  }

  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatStatusLabel(value: string | null) {
  if (!value) {
    return 'No KYB';
  }

  return value.replace(/([A-Z])/g, ' $1').trim();
}

function statusTone(value: string | null) {
  switch (value) {
    case 'Approved':
      return 'teal' as const;
    case 'Rejected':
      return 'rose' as const;
    case 'RequiresAdditionalInfo':
    case 'InProgress':
      return 'amber' as const;
    default:
      return 'neutral' as const;
  }
}

function formatDays(value: number | null) {
  if (value === null) {
    return '—';
  }

  if (value < 1) {
    return `${Math.round(value * 24)}h`;
  }

  return `${value.toFixed(1)}d`;
}

function formatHours(value: number | null) {
  if (value === null) {
    return '—';
  }

  if (value < 1) {
    return `${Math.round(value * 60)}m`;
  }

  if (value < 48) {
    return `${value.toFixed(1)}h`;
  }

  return `${(value / 24).toFixed(1)}d`;
}

function formatSeconds(value: number | null) {
  if (value === null) {
    return '—';
  }

  if (value < 60) {
    return `${Math.round(value)}s`;
  }

  if (value < 3600) {
    return `${Math.round(value / 60)}m`;
  }

  return `${(value / 3600).toFixed(1)}h`;
}
